import { cloneDeep } from 'lodash'
import { StateCreator } from 'zustand'

import { MAX_RECENT_LOGIN_AS_USERS } from '~/features/admin/constants'
import {
  TRecentLoginAsUserEntry,
  TRecentLoginAsUserFields,
} from '~/features/admin/models'
import { TUser } from '~/features/user/models'

type TAdminPortalDefinition = {
  recentLoginAsUsers: TRecentLoginAsUserEntry[]
}

type TAdminPortal = {
  adminPortal: TAdminPortalDefinition
}

export type TAdminPortalState = TAdminPortal & {
  recordRecentLoginAsUser: (user: TRecentLoginAsUserFields) => void
}

export const initialAdminPortalState: TAdminPortalDefinition = {
  recentLoginAsUsers: [],
}

function sortRecentLoginAsUsersByNewest(
  entries: TRecentLoginAsUserEntry[],
): TRecentLoginAsUserEntry[] {
  return [...entries].sort(
    (a, b) =>
      new Date(b.lastUsedAt).getTime() - new Date(a.lastUsedAt).getTime(),
  )
}

export function toRecentLoginAsUserFieldsFromPortalUser(
  user: Pick<TUser, 'id' | 'email' | 'cognitoUid' | 'orgId'>,
): TRecentLoginAsUserFields {
  return {
    id: user.id,
    email: user.email,
    cognitoUid: user.cognitoUid,
    orgId: user.orgId,
  }
}

const createAdminPortalSlice: StateCreator<
  TAdminPortalState,
  [['zustand/devtools', unknown]],
  []
> = (set) => ({
  adminPortal: cloneDeep(initialAdminPortalState),
  recordRecentLoginAsUser: (userFields) =>
    set(
      (state) => {
        const now = new Date().toISOString()
        const withoutSameId = state.adminPortal.recentLoginAsUsers.filter(
          (u) => u.id !== userFields.id,
        )
        const nextEntry: TRecentLoginAsUserEntry = {
          ...userFields,
          lastUsedAt: now,
        }
        const combined = [...withoutSameId, nextEntry]
        const sorted = sortRecentLoginAsUsersByNewest(combined)
        const capped = sorted.slice(0, MAX_RECENT_LOGIN_AS_USERS)
        return {
          adminPortal: {
            ...state.adminPortal,
            recentLoginAsUsers: capped,
          },
        }
      },
      undefined,
      {
        type: 'recordRecentLoginAsUser',
        userFields,
      },
    ),
})

export default createAdminPortalSlice
